/**
 * Unterrichtsstunden je Klasse, Fach und Monat.
 *
 * Der Stundenplan beantwortet die Frage der laufenden Woche, diese Seite die Frage
 * ueber die ganze Ausbildung: wie viel ist in einem Fach gelaufen, wie verteilt es
 * sich ueber die Monate und wie viel fehlt noch zum Lehrplan.
 *
 * Zeilen sind immer die Faecher einer Klasse. Ist zusaetzlich ein Schueler gewaehlt,
 * stehen in den Zellen seine besuchten statt der gehaltenen Stunden.
 */

import { api } from "../api";
import type { LessonReport, ReportAbsence, ReportCell, ReportStageBand, SchoolClass, Student } from "../types";
import { initDropdownSelect, DropdownSelectHandle } from "../components/dropdown-check";
import { showToast } from "../utils/toast";
import { openModal } from "../components/modal";
import { Icons } from "../utils/icons";
import { fitOnResize } from "../utils/fit-height";
import { exportUnterrichtsstunden } from "../utils/export-service";
import { layoutStickyTotals } from "../utils/sticky-totals";
import { esc } from "../utils/html";

type Unit = "std" | "ue";

const UNIT_KEY = "mediskill.auswertung.einheit";
const MONTH_SHORT = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

let classes: SchoolClass[] = [];
let students: Student[] = [];
let report: LessonReport | null = null;
let selectedClassId: number | null = null;
/** null = ganze Klasse, sonst der ausgewertete Schueler. */
let selectedStudentId: number | null = null;
/** "kurs", "stufe:<n>" oder "frei". */
let period = "kurs";
let freeVon = "";
let freeBis = "";
let unit: Unit = "ue";
let gran: Gran = "quartal";
/** Faecher, deren Aufschluesselung ausgeklappt ist. */
const expanded = new Set<number>();
let classDropdown: DropdownSelectHandle | null = null;
let periodDropdown: DropdownSelectHandle | null = null;
let studentDropdown: DropdownSelectHandle | null = null;

/** "2024-09" wird zu "Sep 24". */
function monthLabel(monat: string): string {
  const [y, m] = monat.split("-").map(Number);
  return `${MONTH_SHORT[m - 1]} ${String(y).slice(2)}`;
}

/** Zahlen deutsch und knapp: eine Nachkommastelle genuegt, ganze Zahlen ohne. */
function fmt(n: number): string {
  return n.toLocaleString("de-DE", { maximumFractionDigits: 1 });
}

/** Leere Zelle statt einer Wand aus Nullen: bei 36 Monatsspalten zaehlt jede Zeile Ruhe. */
function fmtCell(n: number): string {
  return n > 0 ? fmt(n) : "";
}

function unitLabel(): string {
  return unit === "std" ? "Std." : "UE";
}

/** Unterrichtseinheiten sind der Normalfall im Schulalltag, deshalb die Vorgabe. */
function storedUnit(): Unit {
  return localStorage.getItem(UNIT_KEY) === "std" ? "std" : "ue";
}

/** Quartale sind die Vorgabe: 36 Monatsspalten ueberblickt niemand, drei Jahre sind zu grob. */
function storedGran(): Gran {
  const v = localStorage.getItem(GRAN_KEY);
  return v === "jahr" || v === "monat" ? v : "quartal";
}

/** Lehrplanwerte stehen in UE und werden fuer die Stundenansicht umgerechnet. */
function sollInUnit(ue: number | null, ueMinuten: number): number | null {
  if (ue === null) return null;
  return unit === "ue" ? ue : Math.round((ue * ueMinuten) / 60 * 10) / 10;
}

// ── Zeilen aufbauen ──

const KINDS = ["unentschuldigt", "entschuldigt", "beurlaubt", "gemeldet"] as const;
const KIND_LABEL: Record<string, string> = {
  unentschuldigt: "unentschuldigt",
  entschuldigt: "entschuldigt",
  beurlaubt: "beurlaubt",
  gemeldet: "noch offen",
};

interface MonthValue {
  held: number;
  planned: number;
  cancelled: number;
  /** Nur in der Schueleransicht gefuellt. */
  missed: ReportAbsence | null;
}
interface Row {
  id: number;
  /** Eine Faechergruppe fasst ihre Faecher zusammen und zaehlt in keiner Summe mit. */
  kind: "fach" | "gruppe";
  label: string;
  color: string | null;
  months: MonthValue[];
  /** Was die Schule ansetzt. null = kein Wert (Mehrklassenansicht oder Fach ohne Lehrplanwert). */
  soll: number | null;
  /** Pflicht laut Verordnung: an der Gruppe oder am Fach ohne Gruppe. */
  pflicht: number | null;
  /** Fach in einer Gruppe: wird eingerueckt. */
  inGroup: boolean;
}

function emptyMonths(count: number, withMissed: boolean): MonthValue[] {
  return Array.from({ length: count }, () => ({
    held: 0, planned: 0, cancelled: 0,
    missed: withMissed ? { unentschuldigt: 0, entschuldigt: 0, beurlaubt: 0, gemeldet: 0 } : null,
  }));
}

function missedSum(a: ReportAbsence | null): number {
  return a ? a.unentschuldigt + a.entschuldigt + a.beurlaubt + a.gemeldet : 0;
}

/** Was in der Zelle steht: besucht in der Schueleransicht, sonst gehalten. */
function shownValue(m: MonthValue): number {
  return m.missed ? Math.round((m.held - missedSum(m.missed)) * 10) / 10 : m.held;
}

function addMissed(into: ReportAbsence, from: ReportAbsence | undefined): void {
  if (!from) return;
  for (const k of KINDS) into[k] += from[k] || 0;
}

/** Zeilen der Tabelle: die Faecher der Klasse, bei gewaehltem Schueler mit dessen Fehlzeiten. */
function buildRows(data: LessonReport): Row[] {
  const monthIndex = new Map(data.monate.map((m, i) => [m, i]));
  const held = (c: ReportCell) => (unit === "std" ? c.stdGehalten : c.ueGehalten);
  const planned = (c: ReportCell) => (unit === "std" ? c.stdGeplant : c.ueGeplant);
  const cancelled = (c: ReportCell) => (unit === "std" ? c.stdEntfallen : c.ueEntfallen);
  const missed = (c: ReportCell) => (unit === "std" ? c.stdVersaeumt : c.ueVersaeumt);
  const withMissed = data.schueler !== null;

  const groups = new Map((data.lehrplan?.gruppen ?? []).map(g => [g.id, g]));
  const rows = new Map<number, Row>();
  for (const f of data.faecher) {
    const inGroup = f.groupId !== null && groups.has(f.groupId);
    rows.set(f.id, {
      id: f.id,
      kind: "fach",
      label: f.name,
      color: f.color,
      months: emptyMonths(data.monate.length, withMissed),
      soll: sollInUnit(f.schuleUe, data.ueMinuten),
      pflicht: inGroup ? null : sollInUnit(f.pflichtUe, data.ueMinuten),
      inGroup,
    });
  }

  for (const c of data.zellen) {
    const row = rows.get(c.subjectId);
    const i = monthIndex.get(c.monat);
    if (!row || i === undefined) continue;
    row.months[i].held += held(c);
    row.months[i].planned += planned(c);
    row.months[i].cancelled += cancelled(c);
    if (row.months[i].missed) addMissed(row.months[i].missed!, missed(c));
  }

  // Faechergruppen wie in der Stundentafel: die Gruppe steht ueber ihren Faechern, an der
  // Stelle des ersten. Ihre Werte sind die Summe der Faecher, ihre Pflicht die der
  // Verordnung. Gruppenzeilen zaehlen in keiner Summe mit, sonst stuende alles doppelt.
  const out: Row[] = [];
  const placed = new Set<number>();
  for (const f of data.faecher) {
    const row = rows.get(f.id)!;
    if (!row.inGroup) { out.push(row); continue; }
    if (placed.has(f.groupId!)) continue;
    placed.add(f.groupId!);
    const g = groups.get(f.groupId!)!;
    const members = data.faecher.filter(x => x.groupId === g.id).map(x => rows.get(x.id)!);
    const months = emptyMonths(data.monate.length, withMissed);
    for (const m of members) {
      m.months.forEach((v, i) => {
        months[i].held += v.held; months[i].planned += v.planned; months[i].cancelled += v.cancelled;
        if (months[i].missed && v.missed) addMissed(months[i].missed!, v.missed);
      });
    }
    const sollWerte = members.map(m => m.soll).filter((v): v is number => v !== null);
    out.push({
      id: -g.id, kind: "gruppe", label: g.name, color: null, months,
      soll: sollWerte.length ? round1(sollWerte.reduce((a, b) => a + b, 0)) : null,
      pflicht: sollInUnit(g.pflichtUe, data.ueMinuten),
      inGroup: false,
    });
    out.push(...members);
  }
  return out;
}

/** Zeilen, die in Summen zaehlen: die Faecher, nicht die Gruppen darueber. */
function countable(rows: Row[]): Row[] {
  return rows.filter(r => r.kind === "fach");
}

/**
 * Stand eines Fachs gegen den Wert der Schule, wie in den Stundentafeln der Schulen
 * farbig markiert: fertig, oder fertig und darueber hinaus.
 */
/**
 * Ziel für den Fortschritt. Bei Fächern der Wert der Schule. Bei Gruppen die Summe der
 * Schulwerte ihrer Fächer, und wenn die Fächer keine haben, die Pflicht der Gruppe laut
 * Verordnung: so zeigt die Gruppe auch dann, wie weit sie ist, wenn die Schule nur die
 * Gruppenpflicht kennt.
 */
function zielOf(r: Row): number | null {
  return r.kind === "gruppe" ? (r.soll ?? r.pflicht) : r.soll;
}

function rowState(r: Row, ist: number): "fertig" | "ueber" | null {
  if (r.soll === null || r.soll <= 0) return null;
  if (ist > r.soll) return "ueber";
  if (ist >= r.soll) return "fertig";
  return null;
}

// ── Zeitachse ──

type Gran = "jahr" | "quartal" | "monat";
const GRAN_KEY = "mediskill.auswertung.zeitachse";
const GRAN_LABEL: Record<Gran, string> = { jahr: "Jahr", quartal: "Quartal", monat: "Monat" };

/** Eine Saeule der Tabelle: die Monatsspalten, die sie zusammenfasst. */
interface Bucket {
  label: string;
  /** Ueberschrift der Bandzeile darueber, leer = keine. */
  band: string;
  /** Positionen in Row.months. */
  idx: number[];
}

/**
 * Monate zu Saeulen zusammenfassen.
 *
 * Gerechnet wird an den Stufenbaendern, nicht am Kalender: ein Kurs, der im September
 * beginnt, hat sein erstes Quartal von September bis November, nicht von Juli bis
 * September. Ein Jahr ist deshalb das Ausbildungsjahr und nicht das Kalenderjahr.
 */
function buildBuckets(monate: string[], bands: ReportStageBand[]): Bucket[] {
  const bandLabel = (m: string) =>
    bands.find(b => b.von.slice(0, 7) <= m && m <= b.bis.slice(0, 7))?.label ?? "";

  if (gran === "monat") {
    return monate.map((m, i) => ({ label: monthLabel(m), band: bandLabel(m), idx: [i] }));
  }

  const out: Bucket[] = [];
  let offen: Bucket | null = null;
  let seitBandbeginn = 0;
  let letztesBand: string | null = null;

  monate.forEach((m, i) => {
    const band = bandLabel(m);
    if (band !== letztesBand) { letztesBand = band; seitBandbeginn = 0; offen = null; }
    // Ein Jahr laeuft bis zum naechsten Band, ein Quartal drei Monate ab Bandbeginn.
    if (offen === null || (gran === "quartal" && seitBandbeginn % 3 === 0)) {
      offen = {
        label: gran === "jahr" ? (band || m.slice(0, 4)) : `Q${Math.floor(seitBandbeginn / 3) + 1}`,
        band: gran === "jahr" ? "" : band,
        idx: [],
      };
      out.push(offen);
    }
    offen.idx.push(i);
    seitBandbeginn++;
  });
  return out;
}

/** Baender ueber den Saeulen. Alle leer = die Zeile faellt weg. */
function bandSpans(buckets: Bucket[]): { label: string; span: number }[] {
  const out: { label: string; span: number }[] = [];
  for (const b of buckets) {
    const last = out[out.length - 1];
    if (last && last.label === b.band) last.span++;
    else out.push({ label: b.band, span: 1 });
  }
  return out.some(b => b.label !== "") ? out : [];
}

/** Werte einer Saeule: die Summe ihrer Monate. */
function aggregate(months: MonthValue[], idx: number[]): MonthValue {
  const out: MonthValue = { held: 0, planned: 0, cancelled: 0, missed: null };
  for (const i of idx) {
    const m = months[i];
    out.held += m.held; out.planned += m.planned; out.cancelled += m.cancelled;
    if (m.missed) {
      if (!out.missed) out.missed = { unentschuldigt: 0, entschuldigt: 0, beurlaubt: 0, gemeldet: 0 };
      addMissed(out.missed, m.missed);
    }
  }
  out.held = round1(out.held); out.planned = round1(out.planned); out.cancelled = round1(out.cancelled);
  return out;
}

// ── Gesamtblock rechts ──

interface TotalCol {
  label: string;
  /** Der Wert als Text. Genau das steht auch im Export. */
  cell: (r: Row, ist: number) => string;
  /** Spaltensumme in der Fusszeile. */
  foot: (rows: Row[], grandIst: number) => string;
  /** Zusatz unter dem Wert, etwa ein Balken. Nur Anzeige, nicht im Export. */
  extra?: (r: Row, ist: number) => string;
  /** Erlaeuterung am Mauszeiger. */
  title?: (r: Row, ist: number) => string;
}

/** Aufgehellte Fachfarbe fuer den noch offenen Teil des Balkens.
 *  Zwilling von lightenColor in components/progress-matrix.ts. */
function lighten(hex: string): string {
  const v = (i: number) => parseInt(hex.slice(i, i + 2), 16) || 0;
  const mix = (c: number) => Math.round(c * 0.45 + 255 * 0.55);
  return `rgb(${mix(v(1))},${mix(v(3))},${mix(v(5))})`;
}

/**
 * Wie weit der Kurs heute ist, zwischen 0 und 1. Bezugsgroesse ist immer die volle
 * Ausbildung, nie der angezeigte Ausschnitt: die Lehrplanstunden gelten fuer den
 * ganzen Kurs, ein Filter auf ein Ausbildungsjahr darf den Massstab nicht verschieben.
 */
function courseProgress(k: { kursVon: string | null; kursBis: string | null } | undefined): number | null {
  if (!k?.kursVon || !k?.kursBis) return null;
  const tage = (iso: string) => new Date(`${iso}T00:00:00`).getTime();
  const von = tage(k.kursVon), bis = tage(k.kursBis), heute = Date.now();
  if (bis <= von) return null;
  return Math.max(0, Math.min(1, (heute - von) / (bis - von)));
}

/**
 * Welche Spalten rechts stehen. Die Liste ist der einzige Ort, an dem das haengt;
 * Kopf, Zellen, Fusszeile, Export und die Rechtsabstaende der fixierten Spalten
 * leiten sich daraus ab.
 *
 * Bewusst kurz gehalten. Verplant, Entfallen und Noch offen hingen rechnerisch
 * voneinander ab (Verplant = Gehalten + Noch offen + Entfallen); vier Zahlen fuer
 * drei Freiheitsgrade lesen sich nicht von selbst. Sie stecken jetzt im Balken und
 * seiner Erlaeuterung.
 *
 * "Lehrplan" ist die Vorgabe von aussen (subjects.total_hours, gepflegt unter
 * Einstellungen, Faecher), nicht das, was die Schule angesetzt hat.
 */
function totalCols(
  rows: Row[], isStudent: boolean, withSoll: boolean, fortschritt: number | null,
  lehrplan: LessonReport["lehrplan"], ueMinuten: number,
): TotalCol[] {
  const sumSoll = (rs: Row[]) => countable(rs).reduce((a, r) => a + (r.soll ?? 0), 0);
  const sumPflicht = (rs: Row[]) => rs.reduce((a, r) => a + (r.pflicht ?? 0), 0);
  const withPflicht = rows.some(r => r.pflicht !== null);
  const totalVerordnung = sollInUnit(lehrplan?.totalUe ?? null, ueMinuten);
  const per = (r: Row, pick: (m: MonthValue) => number) => round1(r.months.reduce((a, m) => a + pick(m), 0));
  const all = (rs: Row[], pick: (m: MonthValue) => number) => round1(rs.reduce((a, r) => a + per(r, pick), 0));
  const cols: TotalCol[] = [];

  cols.push({
    label: isStudent ? "Besucht" : "Gehalten",
    cell: (_r, ist) => fmt(ist),
    foot: (_rs, grand) => fmt(grand),
    title: (r, ist) => {
      const state = rowState(r, ist);
      if (r.kind === "gruppe") return "Summe der Fächer dieser Gruppe";
      return state === "ueber" ? `Fach fertig und ${fmt(round1(ist - r.soll!))} ${unitLabel()} über dem Wert der Schule`
        : state === "fertig" ? "Fach fertig" : "";
    },
  });

  if (isStudent) {
    const missedBy = (m: MonthValue, k?: typeof KINDS[number]) => (k ? (m.missed?.[k] ?? 0) : missedSum(m.missed));
    cols.push({
      label: "Versäumt",
      cell: r => fmtCell(per(r, m => missedBy(m))),
      foot: rs => fmtCell(all(rs, m => missedBy(m))),
      title: r => {
        const teile = KINDS.filter(k => per(r, m => missedBy(m, k)) > 0)
          .map(k => `${fmt(per(r, m => missedBy(m, k)))} ${KIND_LABEL[k]}`);
        return teile.length > 0 ? `Versäumt: ${teile.join(", ")}` : "Nichts versäumt";
      },
    });
  }

  if (!withSoll) return cols;

  cols.push({
    label: "Schule",
    cell: r => (r.soll !== null ? fmt(r.soll) : ""),
    foot: rs => (sumSoll(rs) > 0 ? fmt(round1(sumSoll(rs))) : ""),
    title: r => r.kind === "gruppe" ? "Summe der Werte, die die Schule für die Fächer ansetzt" : "Was die Schule für dieses Fach ansetzt",
  });

  if (withPflicht || totalVerordnung !== null) {
    cols.push({
      label: "Verordnung",
      cell: r => (r.pflicht !== null ? fmt(r.pflicht) : ""),
      // Der Gesamtumfang laut Verordnung, sonst die Summe der fest vergebenen Werte.
      foot: rs => totalVerordnung !== null ? fmt(totalVerordnung) : (sumPflicht(rs) > 0 ? fmt(round1(sumPflicht(rs))) : ""),
      title: r => {
        if (r.pflicht === null) return r.inGroup ? "Die Pflicht steht an der Fächergruppe" : "";
        // Ohne Schulwerte der Fächer gibt es nichts zu vergleichen; die Gruppe misst sich
        // dann direkt an der Pflicht (siehe Fortschritt).
        if (r.kind === "gruppe" && r.soll === null) return "Pflicht laut Verordnung für die ganze Gruppe";
        const schule = r.soll ?? 0;
        return schule < r.pflicht
          ? `Die Schule setzt ${fmt(round1(r.pflicht - schule))} ${unitLabel()} weniger an, als die Verordnung verlangt`
          : "Mindestumfang laut Verordnung";
      },
    });
  }

  cols.push({
    label: "Fortschritt",
    cell: (r, ist) => { const z = zielOf(r); return z && z > 0 ? `${Math.round((ist / z) * 100)} %` : ""; },
    foot: (rs, grand) => (sumSoll(rs) > 0 ? `${Math.round((grand / sumSoll(rs)) * 100)} %` : ""),
    extra: (r, ist) => {
      const z = zielOf(r);
      if (!z || z <= 0) return "";
      const offen = per(r, m => m.planned);
      const pct = (n: number) => Math.max(0, Math.min(100, (n / z) * 100));
      // Gruppen haben keine eigene Farbe; ihr Balken nimmt die Grundfarbe.
      const farbe = r.color || "#2563eb";
      const marke = fortschritt === null ? "" :
        `<div class="aw-bar-mark" style="left:${(fortschritt * 100).toFixed(1)}%"></div>`;
      return `<div class="aw-bar">
        <div class="aw-bar-fill" style="width:${pct(ist + offen)}%;background:${lighten(farbe)}"></div>
        <div class="aw-bar-fill" style="width:${pct(ist)}%;background:${esc(farbe)}"></div>
        ${marke}</div>`;
    },
    title: (r, ist) => {
      const z = zielOf(r);
      if (!z || z <= 0) {
        return r.kind === "gruppe" ? "Für diese Gruppe sind keine Lehrplanstunden hinterlegt."
          : r.inGroup ? "Das Fach hat keine eigenen UE; den Fortschritt zeigt die Gruppe."
          : "Für dieses Fach sind keine Lehrplanstunden hinterlegt.";
      }
      const offen = per(r, m => m.planned);
      const aus = per(r, m => m.cancelled);
      const gegenPflicht = r.kind === "gruppe" && r.soll === null;
      const teile = [`${fmt(ist)} von ${fmt(z)} ${unitLabel()}${gegenPflicht ? " der Pflicht laut Verordnung" : ""}`];
      if (offen > 0) teile.push(`${fmt(offen)} stehen noch im Plan`);
      if (aus > 0) teile.push(`${fmt(aus)} sind ausgefallen`);
      if (fortschritt !== null) teile.push(`der Kurs ist zu ${Math.round(fortschritt * 100)} % durch`);
      return teile.join(", ");
    },
  });

  return cols;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

// ── Aufschluesselung einer Zeile ──

/** Eine Kennzahl der aufgeklappten Zeile, ueber dieselben Saeulen wie die Tabelle. */
interface DetailSpec {
  label: string;
  value: (m: MonthValue) => number;
  /** Untergliederung, wird eingerueckt und blasser gesetzt. */
  sub?: boolean;
}

/**
 * Die dritte Dimension: neben Fach und Zeit steht in jeder Zelle nur eine von mehreren
 * Kennzahlen. Ausgeklappt bekommt jede eine eigene Zeile, ausgerichtet an denselben
 * Saeulen. Das ersetzt die Erlaeuterung am Mauszeiger, die es auf einem Tablet nicht
 * gibt, und macht Spalten vergleichbar statt nur einzelne Zellen lesbar.
 */
function detailSpecs(isStudent: boolean, r: Row): DetailSpec[] {
  if (!isStudent) {
    return [
      { label: "Verplant", value: m => m.held + m.planned + m.cancelled },
      { label: "davon stattgefunden", value: m => m.held, sub: true },
      { label: "davon entfallen", value: m => m.cancelled, sub: true },
      { label: "noch offen", value: m => m.planned },
    ];
  }
  const specs: DetailSpec[] = [
    { label: "Stattgefunden", value: m => m.held },
    { label: "davon versäumt", value: m => missedSum(m.missed), sub: true },
  ];
  // Nur die Arten zeigen, die in diesem Fach ueberhaupt vorkommen.
  for (const k of KINDS) {
    if (r.months.some(m => (m.missed?.[k] ?? 0) > 0)) {
      specs.push({ label: KIND_LABEL[k], value: m => m.missed?.[k] ?? 0, sub: true });
    }
  }
  specs.push({ label: "Besucht", value: m => m.held - missedSum(m.missed) });
  return specs;
}

// ── Tabelle zeichnen ──

function renderTable(host: HTMLElement, data: LessonReport): void {
  // Beim Aufklappen und beim Umschalten der Zeitachse soll die Ansicht stehen bleiben.
  const top = host.scrollTop, left = host.scrollLeft;
  const isStudent = data.schueler !== null;
  const rows = buildRows(data);
  const buckets = buildBuckets(data.monate, data.klassen[0]?.stageBands || []);
  const bands = bandSpans(buckets);
  const withSoll = rows.some(r => r.soll !== null || r.pflicht !== null);
  const nameLabel = "Fach";
  const cols = totalCols(rows, isStudent, withSoll, courseProgress(data.klassen[0]), data.lehrplan, data.ueMinuten);

  // Werte je Saeule einmal vorrechnen, sie werden fuer Zellen und Summen gebraucht.
  // Gruppenzeilen bleiben aus den Spaltensummen heraus, ihre Faecher zaehlen schon.
  const zellen = rows.map(r => buckets.map(b => aggregate(r.months, b.idx)));
  const zaehlt = rows.map(r => r.kind === "fach");
  const spaltenSumme = buckets.map((_b, bi) => round1(zellen.reduce((a, z, ri) => a + (zaehlt[ri] ? shownValue(z[bi]) : 0), 0)));
  // Spalten, in denen noch nichts gelaufen ist, zeigen in der Fusszeile die geplante
  // Summe, passend zu den kursiven Zahlen darueber. Sonst staende dort 0 unter lauter
  // Zahlen.
  const spaltenOffen = buckets.map((_b, bi) => round1(zellen.reduce((a, z, ri) => a + (zaehlt[ri] ? z[bi].planned : 0), 0)));
  const nurGeplant = buckets.map((_b, bi) => spaltenSumme[bi] === 0 && spaltenOffen[bi] > 0);
  const grandIst = round1(spaltenSumme.reduce((a, b) => a + b, 0));

  // Zweite Kopfzeile nur, wenn es Baender ueber den Saeulen gibt. In der Jahresansicht
  // sind die Saeulen selbst die Ausbildungsjahre, dort waere sie leer. Ueber dem
  // Gesamtblock steht bewusst keine Sammelueberschrift mehr: Gehalten, Lehrplan und
  // Fortschritt sagen fuer sich, was sie sind.
  const zweizeilig = bands.length > 0;
  const head1 = zweizeilig ? `
    <tr>
      <th class="aw-name"></th>
      ${bands.map(b => `<th class="aw-band" colspan="${b.span}">${esc(b.label)}</th>`).join("")}
      <th class="aw-total aw-total-first" colspan="${cols.length}">Gesamt</th>
    </tr>` : "";

  const head2 = `
    <tr>
      <th class="aw-name">${nameLabel}</th>
      ${buckets.map(b => `<th class="aw-month">${esc(b.label)}</th>`).join("")}
      ${cols.map((c, i) => `<th class="aw-total${i === 0 ? " aw-total-first" : ""}">${esc(c.label)}</th>`).join("")}
    </tr>`;

  // "gelaufen/geplant" in einer Zelle, mit dem Trenner in eigener Farbe, damit die
  // beiden Zahlen nicht zu einer verschmelzen.
  const splitHtml = (offen: number) =>
    `<span class="aw-split">/</span><span class="aw-future">${fmt(offen)}</span>`;

  const body = rows.map((r, ri) => {
    const ist = round1(zellen[ri].reduce((a, z) => a + shownValue(z), 0));

    const cells = zellen[ri].map(z => {
      const wert = shownValue(z);
      const fehlt = z.missed ? round1(missedSum(z.missed)) : 0;
      if (z.held === 0 && z.planned === 0) return `<td></td>`;

      const parts: string[] = [];
      if (isStudent) parts.push(`${fmt(wert)} ${unitLabel()} besucht von ${fmt(z.held)} gehalten`);
      else parts.push(`${fmt(z.held)} ${unitLabel()} gehalten`);
      if (fehlt > 0) {
        const nach = KINDS.filter(k => z.missed![k] > 0).map(k => `${fmt(round1(z.missed![k]))} ${KIND_LABEL[k]}`);
        parts.push(`versäumt: ${nach.join(", ")}`);
      }
      if (z.planned > 0) parts.push(`${fmt(z.planned)} noch offen`);
      if (z.cancelled > 0) parts.push(`${fmt(z.cancelled)} entfallen`);

      // Noch nichts gelaufen, aber etwas angesetzt: nur die geplante Zahl, kursiv und
      // blass. Sonst waere die ganze Zukunft leer und von einem Zeitraum ohne
      // Unterricht nicht zu unterscheiden.
      if (z.held === 0) {
        return `<td class="aw-future" title="${esc(parts.join(", "))}">${fmt(z.planned)}</td>`;
      }
      // Angebrochener Zeitraum: beides in einer Zelle, jede Zahl in ihrer eigenen
      // Schreibweise. Aufrecht ist gelaufen, kursiv steht noch aus.
      const zahl = z.planned > 0
        ? `${fmt(wert)}${splitHtml(z.planned)}`
        : fmt(wert);
      // Nur in der Schueleransicht steht ein Wert darunter, und dort die Fehlstunden.
      const zusatz = isStudent && fehlt > 0 ? `<span class="aw-missed">-${fmt(fehlt)}</span>` : "";
      return `<td title="${esc(parts.join(", "))}">${zahl}${zusatz}</td>`;
    }).join("");

    const totalCells = cols.map((c, i) => {
      const t = c.title?.(r, ist);
      return `<td class="aw-total${i === 0 ? " aw-total-first" : ""}"${t ? ` title="${esc(t)}"` : ""}>${
        c.cell(r, ist)}${c.extra?.(r, ist) ?? ""}</td>`;
    }).join("");

    const offen = r.kind === "fach" && expanded.has(r.id);
    const specs = offen ? detailSpecs(isStudent, r) : [];
    const details = specs.map((spec, si) => {
      const werte = zellen[ri].map(z => round1(spec.value(z)));
      const summe = round1(werte.reduce((a, b) => a + b, 0));
      // Die letzte Kennzahl schliesst den Block ab. Ueber :last-of-type geht das nicht:
      // das zaehlt tr innerhalb von tbody, nicht tr einer Klasse.
      const letzte = si === specs.length - 1 ? " aw-detail-last" : "";
      return `<tr class="aw-detail${spec.sub ? " aw-detail-sub" : ""}${letzte}">
        <td class="aw-name">${esc(spec.label)}</td>
        ${werte.map(w => `<td>${fmtCell(w)}</td>`).join("")}
        ${cols.map((_c, i) => `<td class="aw-total${i === 0 ? " aw-total-first" : ""}">${
          i === 0 ? fmtCell(summe) : ""}</td>`).join("")}
      </tr>`;
    }).join("");

    if (r.kind === "gruppe") {
      return `<tr class="aw-group">
        <td class="aw-name" title="${esc(r.label)}"><span class="aw-subject"><span class="aw-label">${esc(r.label)}</span></span></td>
        ${cells}${totalCells}</tr>`;
    }
    const state = rowState(r, ist);
    const stateBadge = state === "ueber" ? `<span class="aw-state aw-state-over" title="Fach fertig und über dem Wert der Schule">✓+</span>`
      : state === "fertig" ? `<span class="aw-state aw-state-done" title="Fach fertig">✓</span>` : "";
    return `<tr class="aw-row${offen ? " aw-row-open" : ""}${state ? ` aw-${state}` : ""}${r.inGroup ? " aw-in-group" : ""}" data-fach="${r.id}">
      <td class="aw-name" title="${esc(r.label)}"><span class="aw-subject">
        <span class="aw-caret"></span>${
        r.color ? `<span class="aw-dot" style="background:${esc(r.color)}"></span>` : ""}<span class="aw-label">${esc(r.label)}</span>${stateBadge}</span></td>
      ${cells}${totalCells}</tr>${details}`;
  }).join("");

  host.innerHTML = `
    <table class="aw-table">
      <thead>${head1}${head2}</thead>
      <tbody>${body}</tbody>
      <tfoot><tr>
        <td class="aw-name">Summe</td>
        ${spaltenSumme.map((s, bi) => {
          if (nurGeplant[bi]) return `<td class="aw-future">${fmt(spaltenOffen[bi])}</td>`;
          if (s === 0) return `<td class="aw-zero">0</td>`;
          return `<td>${fmt(s)}${spaltenOffen[bi] > 0 ? splitHtml(spaltenOffen[bi]) : ""}</td>`;
        }).join("")}
        ${cols.map((c, i) => `<td class="aw-total${i === 0 ? " aw-total-first" : ""}">${c.foot(rows, grandIst)}</td>`).join("")}
      </tr></tfoot>
    </table>`;

  layoutStickyTotals(host, "aw-total", cols.length);
  host.scrollTop = top; host.scrollLeft = left;
}

// ── Laden ──

/** Zeitraum aus der Auswahl. undefined heisst: das Backend nimmt die ganze Ausbildung. */
function currentRange(): { von?: string; bis?: string } {
  if (period === "frei" && freeVon && freeBis) {
    // Monatsfelder liefern "YYYY-MM"; daraus der erste und der letzte Tag.
    const [by, bm] = freeBis.split("-").map(Number);
    const last = new Date(by, bm, 0).getDate();
    return { von: `${freeVon}-01`, bis: `${freeBis}-${String(last).padStart(2, "0")}` };
  }
  if (period.startsWith("stufe:") && report) {
    const stage = Number(period.slice(6));
    const band = report.klassen[0]?.stageBands.find(b => b.stage === stage);
    if (band) return { von: band.von, bis: band.bis };
  }
  return {};
}

async function loadReport(el: HTMLElement): Promise<void> {
  const table = el.querySelector<HTMLElement>("#aw-table")!;
  table.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;
  const range = currentRange();
  try {
    report = await api.auswertungen.unterricht(
      [selectedClassId!], range.von, range.bis, selectedStudentId ?? undefined);
  } catch (err: any) {
    report = null;
    table.innerHTML = `<div class="aw-empty">Die Auswertung konnte nicht geladen werden: ${esc(err.message)}</div>`;
    return;
  }
  fillPeriodDropdown(el);
  if (report.monate.length === 0) {
    const ohneKlasse = report.schueler && !report.schueler.classId;
    const ohneKurs = report.klassen.filter(k => !k.courseStartDate || !k.hasModel);
    table.innerHTML = `<div class="aw-empty">${
      ohneKlasse
        ? `${esc(report.schueler!.name)} ist keiner Klasse zugeordnet. Ohne Klasse gibt es keinen Unterricht, gegen den sich rechnen ließe.`
        : ohneKurs.length > 0
          ? `Für ${ohneKurs.length === 1 ? "die Klasse" : "die Klassen"} ${esc(ohneKurs.map(k => k.name).join(", "))} fehlt der Kursbeginn oder das Ausbildungsmodell. Beides wird bei der Klasse hinterlegt.`
          : "Für den gewählten Zeitraum gibt es keine Monate."}</div>`;
    return;
  }
  renderTable(table, report);
}

/**
 * Was die Tabelle bedeutet, als Fenster statt als Fliesstext ueber der Tabelle.
 *
 * Der Text war zuletzt fuenf Zeilen lang und hat die Tabelle nach unten gedrueckt,
 * obwohl man ihn nach dem ersten Lesen nicht mehr braucht. Im Fenster ist Platz fuer
 * eine richtige Legende statt fuer aneinandergereihte Saetze.
 */
function openInfo(): void {
  if (!report) return;
  const einheit = unit === "std" ? "Zeitstunden" : "Unterrichtseinheiten";
  const wert = report.schueler
    ? `die ${einheit}, die ${esc(report.schueler.name)} besucht hat`
    : `gehaltene ${einheit}`;

  openModal("Wie diese Auswertung zu lesen ist", `
    <p class="aw-info-lead">In jeder Zelle stehen ${wert}.</p>
    <table class="aw-info-table">
      <tr><td><strong>84</strong></td><td>gelaufen, nichts mehr offen</td></tr>
      <tr><td><strong>18,7</strong><span class="aw-split">/</span><span class="aw-future">8</span></td>
          <td>angebrochen: 18,7 gelaufen, 8 stehen noch aus</td></tr>
      <tr><td><span class="aw-future">36</span></td><td>noch nichts gelaufen, 36 sind geplant</td></tr>
      ${report.schueler ? `<tr><td><strong>20</strong><span class="aw-missed" style="display:inline">-4</span></td>
          <td>20 besucht, 4 versäumt</td></tr>` : ""}
      <tr><td class="text-muted">leer</td><td>weder gelaufen noch geplant</td></tr>
    </table>

    <h3 class="aw-info-head">Zählregeln</h3>
    <ul class="aw-info-list">
      <li>Entfallene Stunden zählen nicht als gehalten, Vertretungen zählen normal mit.</li>
      <li>Gelaufen und noch offen trennt das heutige Datum, nicht der Monatswechsel.</li>
      <li>Eine Unterrichtseinheit zählt so, wie es im Stundenraster am Block steht, ein 90-Minuten-Block also meist als 2 UE.</li>
      ${unit === "std" ? `<li>Die Lehrplanwerte sind mit ${fmt(report.ueMinuten)} Minuten je Unterrichtseinheit in Zeitstunden umgerechnet.</li>` : ""}
    </ul>

    <h3 class="aw-info-head">Mehr sehen</h3>
    <ul class="aw-info-list">
      <li><strong>Ein Klick auf ein Fach</strong> klappt die übrigen Kennzahlen darunter auf, über dieselben Spalten.</li>
      <li>Die <strong>Marke im Fortschrittsbalken</strong> zeigt, wie weit der Kurs heute ist. Liegt der Balken links davon, hinkt das Fach hinterher.</li>
      <li><strong>Schule</strong> ist, was die Schule für das Fach ansetzt, <strong>Verordnung</strong> der Mindestumfang laut Ausbildungs- und Prüfungsverordnung. Beides steht unter Einstellungen, Stundenplan, Lehrplan. Gibt die Verordnung eine Zahl für mehrere Fächer zusammen vor, steht sie an der grauen Gruppenzeile darüber.</li>
      <li><span class="aw-state aw-state-done">✓</span> heißt Fach fertig: gehalten ist, was die Schule ansetzt. <span class="aw-state aw-state-over">✓+</span> heißt fertig und darüber hinaus.</li>
      <li>In der Summenzeile steht unter Verordnung der Gesamtumfang laut Verordnung, sofern er im Lehrplan hinterlegt ist.</li>
    </ul>`, { wide: true });
}

/** Schuelerauswahl der gewaehlten Klasse. */
function fillStudentDropdown(el: HTMLElement): void {
  const drop = el.querySelector<HTMLElement>("#aw-student");
  const panel = drop?.querySelector<HTMLElement>(".dropdown-check-panel");
  if (!drop || !panel) return;
  const eigene = students
    .filter(s => +(s.classId ?? 0) === selectedClassId)
    .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, "de"));

  panel.innerHTML = [
    `<div class="dropdown-check-item" data-value="">Ganze Klasse</div>`,
    ...eigene.map(s => `<div class="dropdown-check-item" data-value="${s.id}">${esc(`${s.lastName}, ${s.firstName}`)}</div>`),
  ].join("");

  // Eine Klasse ohne Schueler laesst nichts zu waehlen.
  const btn = drop.querySelector<HTMLButtonElement>(".dropdown-check-btn")!;
  btn.disabled = eigene.length === 0;
  // Nach einem Klassenwechsel gehoert der zuvor gewaehlte Schueler nicht mehr dazu.
  if (selectedStudentId !== null && !eigene.some(s => +s.id === selectedStudentId)) selectedStudentId = null;
  studentDropdown?.setValue(selectedStudentId === null ? "" : String(selectedStudentId));
}

/** Zeitraum-Auswahl aus den Stufen der geladenen Klasse aufbauen. */
function fillPeriodDropdown(el: HTMLElement): void {
  const panel = el.querySelector<HTMLElement>("#aw-period .dropdown-check-panel");
  if (!panel || !report) return;
  const bands = report.klassen[0]?.stageBands || [];
  panel.innerHTML = [
    `<div class="dropdown-check-item" data-value="kurs">Ganzer Kurs</div>`,
    ...bands.map(b => `<div class="dropdown-check-item" data-value="stufe:${b.stage}">${esc(b.label)}</div>`),
    `<div class="dropdown-check-item" data-value="frei">Freier Zeitraum</div>`,
  ].join("");
  // Eine zuvor gewaehlte Stufe kann es nach einem Klassenwechsel nicht mehr geben.
  if (period.startsWith("stufe:") && !bands.some(b => `stufe:${b.stage}` === period)) period = "kurs";
  periodDropdown?.setValue(period);
  el.querySelector<HTMLElement>("#aw-free")!.classList.toggle("hidden", period !== "frei");
}

// ── Export ──

function exportData(fmtKind: "xlsx" | "pdf"): void {
  if (!report || report.monate.length === 0) { showToast("Es gibt nichts zu exportieren."); return; }
  const isStudent = report.schueler !== null;
  const rows = buildRows(report);
  const buckets = buildBuckets(report.monate, report.klassen[0]?.stageBands || []);
  const bands = bandSpans(buckets);
  const cols = totalCols(rows, isStudent, rows.some(r => r.soll !== null || r.pflicht !== null),
                         courseProgress(report.klassen[0]), report.lehrplan, report.ueMinuten);
  const zellen = rows.map(r => buckets.map(b => aggregate(r.months, b.idx)));
  // Gruppenzeilen nicht mitzaehlen, ihre Faecher stehen schon in der Summe.
  const grandIst = round1(zellen.reduce((a, z, ri) =>
    a + (rows[ri].kind === "fach" ? z.reduce((s, m) => s + shownValue(m), 0) : 0), 0));

  const titel = isStudent
    ? `${report.schueler!.name} (${report.klassen[0]?.name || ""})`
    : (report.klassen[0]?.name || "");

  // Der Export bekommt dieselben Gesamtspalten wie die Ansicht, als Text: welche das
  // sind, entscheidet allein totalCols(), damit Bildschirm und Datei nicht abweichen.
  void exportUnterrichtsstunden(fmtKind, {
    title: titel,
    von: report.von,
    bis: report.bis,
    unitLabel: unitLabel(),
    nameLabel: "Fach",
    monate: buckets.map(b => b.label),
    bands,
    totalHeads: cols.map(c => c.label),
    totalFoot: cols.map(c => c.foot(rows, grandIst)),
    rows: rows.map((r, ri) => {
      const ist = round1(zellen[ri].reduce((a, z) => a + shownValue(z), 0));
      const state = r.kind === "fach" ? rowState(r, ist) : null;
      return {
        // Faecher einer Gruppe eingerueckt, wie auf dem Bildschirm.
        label: (r.inGroup ? "   " : "") + r.label + (state === "ueber" ? " (fertig, über Soll)" : state === "fertig" ? " (fertig)" : ""),
        months: zellen[ri].map(shownValue),
        ist,
        totals: cols.map(c => c.cell(r, ist)),
        group: r.kind === "gruppe",
      };
    }),
  }).catch((err: any) => showToast(`Export fehlgeschlagen: ${err.message}`));
}

// ── Seite ──

export async function render(el: HTMLElement): Promise<void> {
  (el as any).__renderAC?.abort();
  const ac = new AbortController();
  (el as any).__renderAC = ac;

  unit = storedUnit();
  gran = storedGran();

  try {
    const [cls, stu] = await Promise.all([api.classes.list(), api.students.list()]);
    classes = cls.filter(c => c.active);
    students = stu.filter(s => s.active);
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Klassen konnten nicht geladen werden: ${esc(err.message)}</div>`;
    return;
  }
  if (classes.length === 0) {
    el.innerHTML = `<div class="empty-state"><p>Es sind noch keine Klassen angelegt.</p></div>`;
    return;
  }
  // Vorauswahl beim ersten Aufruf: die erste Klasse mit Kursbeginn, denn nur die hat
  // einen Zeitraum. Eine frueher getroffene Auswahl bleibt beim Zurueckkommen stehen.
  if (selectedClassId === null || !classes.some(c => c.id === selectedClassId)) {
    selectedClassId = (classes.find(c => c.courseStartDate) || classes[0]).id;
  }

  el.innerHTML = `
    <div class="page-header"><h1>Unterrichtsstunden</h1></div>
    <div class="aw-wrap">
      <div class="aw-controls">
        <div class="dropdown-check" id="aw-class">
          <button type="button" class="dropdown-check-btn">Klasse</button>
          <div class="dropdown-check-panel dropdown-check-panel-left">
            ${classes.map(c => `<div class="dropdown-check-item" data-value="${c.id}">${esc(c.name)}</div>`).join("")}
          </div>
        </div>
        <div class="dropdown-check" id="aw-student">
          <button type="button" class="dropdown-check-btn">Ganze Klasse</button>
          <div class="dropdown-check-panel"><div class="dropdown-check-item" data-value="">Ganze Klasse</div></div>
        </div>
        <div class="dropdown-check" id="aw-period">
          <button type="button" class="dropdown-check-btn">Zeitraum</button>
          <div class="dropdown-check-panel"><div class="dropdown-check-item" data-value="kurs">Ganzer Kurs</div></div>
        </div>
        <span id="aw-free" class="hidden" style="display:inline-flex;gap:.375rem;align-items:center;font-size:.875rem;color:var(--gray-600)">
          <input type="month" class="aw-date" id="aw-von" data-readable="true" value="${esc(freeVon)}" />
          bis
          <input type="month" class="aw-date" id="aw-bis" data-readable="true" value="${esc(freeBis)}" />
        </span>
        <div class="aw-seg" id="aw-gran">
          ${(["jahr", "quartal", "monat"] as Gran[]).map(g =>
            `<button type="button" data-gran="${g}">${GRAN_LABEL[g]}</button>`).join("")}
        </div>
        <div class="aw-seg" id="aw-unit">
          <button type="button" data-unit="ue">UE</button>
          <button type="button" data-unit="std">Std.</button>
        </div>
        <button type="button" class="btn btn-secondary btn-square" id="aw-info"
                title="Wie diese Auswertung zu lesen ist" aria-label="Wie diese Auswertung zu lesen ist">${Icons.informationCircle}</button>
        <div class="aw-spacer"></div>
        <div class="dropdown-check" id="aw-download">
          <button type="button" class="btn btn-secondary dropdown-check-btn">Download</button>
          <div class="dropdown-check-panel">
            <div class="dropdown-check-item" data-fmt="xlsx">Als Excel</div>
            <div class="dropdown-check-item" data-fmt="pdf">Als PDF</div>
          </div>
        </div>
      </div>
      <div class="aw-table-wrap" id="aw-table"></div>
    </div>`;

  classDropdown = initDropdownSelect(el.querySelector<HTMLElement>("#aw-class")!, value => {
    selectedClassId = Number(value);
    // Die Schueler der neuen Klasse sind andere; die Auswahl faellt gegebenenfalls
    // auf die ganze Klasse zurueck.
    fillStudentDropdown(el);
    void loadReport(el);
  });
  classDropdown.setValue(String(selectedClassId));

  studentDropdown = initDropdownSelect(el.querySelector<HTMLElement>("#aw-student")!, value => {
    selectedStudentId = value === "" ? null : Number(value);
    void loadReport(el);
  });
  fillStudentDropdown(el);

  periodDropdown = initDropdownSelect(el.querySelector<HTMLElement>("#aw-period")!, value => {
    period = value;
    el.querySelector<HTMLElement>("#aw-free")!.classList.toggle("hidden", period !== "frei");
    // Bei "frei" erst laden, wenn beide Monate stehen.
    if (period === "frei" && !(freeVon && freeBis)) return;
    void loadReport(el);
  });
  periodDropdown.setValue(period);

  const onFree = () => {
    freeVon = (el.querySelector("#aw-von") as HTMLInputElement).value;
    freeBis = (el.querySelector("#aw-bis") as HTMLInputElement).value;
    if (freeVon && freeBis) void loadReport(el);
  };
  el.querySelector("#aw-von")!.addEventListener("change", onFree);
  el.querySelector("#aw-bis")!.addEventListener("change", onFree);

  // Zeile aufklappen. Delegation, weil die Tabelle bei jedem Umschalten neu entsteht.
  el.querySelector<HTMLElement>("#aw-table")!.addEventListener("click", e => {
    const tr = (e.target as HTMLElement).closest("tr.aw-row") as HTMLElement | null;
    if (!tr || !report) return;
    const id = Number(tr.dataset.fach);
    if (expanded.has(id)) expanded.delete(id); else expanded.add(id);
    renderTable(el.querySelector<HTMLElement>("#aw-table")!, report);
  });

  el.querySelector("#aw-info")!.addEventListener("click", openInfo);

  const granBox = el.querySelector<HTMLElement>("#aw-gran")!;
  const paintGran = () => granBox.querySelectorAll<HTMLButtonElement>("button")
    .forEach(b => b.classList.toggle("is-active", b.dataset.gran === gran));
  granBox.addEventListener("click", e => {
    const btn = (e.target as HTMLElement).closest("button") as HTMLButtonElement | null;
    if (!btn || btn.dataset.gran === gran) return;
    gran = btn.dataset.gran as Gran;
    localStorage.setItem(GRAN_KEY, gran);
    paintGran();
    // Die Saeulen fassen dieselben Monatswerte nur anders zusammen, kein neuer Aufruf.
    if (report && report.monate.length > 0) renderTable(el.querySelector<HTMLElement>("#aw-table")!, report);
  });
  paintGran();

  const unitBox = el.querySelector<HTMLElement>("#aw-unit")!;
  const paintUnit = () => unitBox.querySelectorAll<HTMLButtonElement>("button")
    .forEach(b => b.classList.toggle("is-active", b.dataset.unit === unit));
  unitBox.addEventListener("click", e => {
    const btn = (e.target as HTMLElement).closest("button") as HTMLButtonElement | null;
    if (!btn || btn.dataset.unit === unit) return;
    unit = btn.dataset.unit as Unit;
    localStorage.setItem(UNIT_KEY, unit);
    paintUnit();
    // Beide Einheiten kommen in derselben Antwort mit, deshalb kein neuer Aufruf.
    if (report && report.monate.length > 0) renderTable(el.querySelector<HTMLElement>("#aw-table")!, report);
  });
  paintUnit();

  const dd = el.querySelector<HTMLElement>("#aw-download")!;
  dd.querySelector(".dropdown-check-btn")!.addEventListener("click", () => dd.classList.toggle("open"));
  dd.querySelector(".dropdown-check-panel")!.addEventListener("click", e => {
    const item = (e.target as HTMLElement).closest(".dropdown-check-item") as HTMLElement | null;
    if (!item) return;
    dd.classList.remove("open");
    exportData(item.dataset.fmt as "xlsx" | "pdf");
  });
  document.addEventListener("click", e => {
    if (!dd.contains(e.target as Node)) dd.classList.remove("open");
  }, { signal: ac.signal });

  fitOnResize(el.querySelector<HTMLElement>("#aw-table")!, ac.signal);
  await loadReport(el);
}
